/**
 * Task definitions for every checklist item on gov solicitations.
 * Each item is a { key, label, type } plus optional type-specific fields.
 *
 * Task types drive the FE renderer (govTaskItem.tsx).
 */

export type TaskType =
  | 'notice_id'          // Read-only: shows SAM link
  | 'docs_check'         // Read-only: shows doc count / link to Documents tab
  | 'assignment'         // User picker → saves to solicitation.assigned_to
  | 'bid_captain'        // User picker → saves to solicitation.bid_captain_id
  | 'decision'           // Go/No-Go + reason → saves to solicitation.go_no_go_decision/reason
  | 'naics_check'        // Confirms NAICS match
  | 'capability_check'   // Radio: strong/moderate/weak + reason
  | 'poc_capture'        // Name / email / phone → saves to solicitation primary_poc_*
  | 'outreach'           // Method / date / notes → task_artifact
  | 'teaming'            // Y/N/NA + partners list → task_artifact
  | 'response_drafted'   // Attaches a response doc (uses documents)
  | 'submission'         // Method / confirmation → saves to solicitation.bid_submission_*
  | 'acknowledgment'     // Was gov acknowledgment received? Attach + date
  | 'follow_up_request'  // Method + date + notes
  | 'meeting'            // Schedule meeting → creates gov_event
  | 'date_confirm'       // Confirm expected date
  | 'notice_id_capture'  // Capture actual notice ID once released
  | 'doc_attach'         // Requires attached doc of a category
  | 'field_text'         // Simple text field on the solicitation
  | 'field_money'        // $ amount field
  | 'field_date'         // Date field
  | 'wage_det'           // Wage determination — doc attach + ref number
  | 'site_visit'         // Site visit event + attended flag
  | 'cost_model'         // Cost/pricing model — doc attach + amount
  | 'simple';            // Fallback: just check + notes

export interface TaskDef {
  key: string;
  label: string;
  type: TaskType;
  hint?: string;
  // For doc_attach and derivatives — which document category satisfies it
  docCategory?: string;
  // Optional field mapping for field_* types (which solicitation column)
  field?: string;
}

// ─── Sources Sought / RFI Pipeline ───────────────────────────────────────────
export const SOURCES_SOUGHT_TASKS: Record<string, TaskDef[]> = {
  identified: [
    { key: 'notice_id_fetched', label: 'Notice ID fetched from SAM.gov', type: 'notice_id',
      hint: 'Verify the SAM.gov link opens the correct solicitation.' },
    { key: 'docs_downloaded',  label: 'Documents downloaded',            type: 'docs_check',
      hint: 'At least one document should be attached.' },
    { key: 'assigned',         label: 'Assigned to bidder',              type: 'assignment' },
  ],
  go_no_go: [
    { key: 'naics_match',     label: 'NAICS match confirmed',            type: 'naics_check' },
    { key: 'capability_fit',  label: 'Capability fit assessed',          type: 'capability_check' },
    { key: 'decision_logged', label: 'Go/No-Go decision + reason logged', type: 'decision' },
  ],
  engage_poc: [
    { key: 'poc_recorded',    label: 'POC name/email/phone recorded',    type: 'poc_capture' },
    { key: 'initial_outreach', label: 'Initial outreach logged',         type: 'outreach' },
  ],
  teaming_assessment: [
    { key: 'teaming_decision',    label: 'Teaming needed decision',      type: 'teaming' },
    { key: 'partners_identified', label: 'Partners identified (or N/A)', type: 'simple',
      hint: 'If you marked Yes above, list partner companies + POCs in your notes.' },
  ],
  write_response: [
    { key: 'capability_statement', label: 'Capability statement attached', type: 'doc_attach',
      docCategory: 'capability_statement' },
    { key: 'past_performance',     label: 'Past performance docs selected', type: 'doc_attach',
      docCategory: 'past_performance' },
    { key: 'response_drafted',     label: 'Response drafted',              type: 'doc_attach',
      docCategory: 'response' },
  ],
  submit_response: [
    { key: 'response_submitted',  label: 'Response submitted',            type: 'submission' },
    { key: 'confirmation_saved',  label: 'Submission confirmation saved', type: 'doc_attach',
      docCategory: 'response',
      hint: 'Attach the submission receipt / email confirmation as a document.' },
  ],
  confirm_receipt: [
    { key: 'acknowledgment_received', label: 'Government acknowledgment received & attached',
      type: 'acknowledgment' },
  ],
  request_follow_up: [
    { key: 'follow_up_requested', label: 'Follow-up requested', type: 'follow_up_request' },
  ],
  demo_capability_brief: [
    { key: 'meeting_scheduled', label: 'Meeting scheduled with date logged', type: 'meeting',
      hint: 'This will add the meeting to the calendar.' },
    { key: 'brief_delivered',   label: 'Brief delivered + notes captured',   type: 'simple' },
  ],
  confirm_solicitation_date: [
    { key: 'release_date_confirmed', label: 'Expected release date confirmed with POC',
      type: 'date_confirm' },
  ],
  solicitation_released: [
    { key: 'notice_id_captured', label: 'Actual solicitation Notice ID captured',
      type: 'notice_id_capture' },
  ],
};

// ─── Active Solicitation Pipeline ────────────────────────────────────────────
export const ACTIVE_TASKS: Record<string, TaskDef[]> = {
  identified: [
    { key: 'notice_id_fetched', label: 'Notice ID fetched from SAM.gov', type: 'notice_id' },
    { key: 'docs_downloaded',   label: 'Documents downloaded',           type: 'docs_check' },
    { key: 'type_set',          label: 'Type set (RFQ/RFP/IFB)',         type: 'field_text',
      field: 'opportunity_type' },
    { key: 'deadline_logged',   label: 'Deadline logged',                type: 'field_date',
      field: 'response_deadline' },
    { key: 'assigned',          label: 'Assigned to bidder',             type: 'assignment' },
  ],
  analyzing: [
    { key: 'solicitation_read',    label: 'Solicitation read',           type: 'simple',
      hint: 'Read through the SOW/PWS in the Documents tab.' },
    { key: 'scope_summarized',     label: 'Scope summarized',            type: 'simple',
      hint: 'Add your scope summary to the notes below.' },
    { key: 'requirements_extracted', label: 'Key requirements extracted', type: 'simple' },
    { key: 'deadline_calendar',    label: 'Deadline added to calendar',  type: 'meeting',
      hint: 'Auto-creates a "Response Due" event on the calendar.' },
  ],
  go_no_go: [
    { key: 'decision_logged',     label: 'Decision + reason logged',     type: 'decision' },
    { key: 'bid_captain_assigned', label: 'Bid captain assigned (if Go)', type: 'bid_captain' },
  ],
  bid_prep: [
    { key: 'teaming_status',      label: 'Teaming status (Y/N + partners)', type: 'teaming' },
    { key: 'capability_statement', label: 'Capability statement attached', type: 'doc_attach',
      docCategory: 'capability_statement' },
    { key: 'past_performance',    label: 'Past performance docs selected', type: 'doc_attach',
      docCategory: 'past_performance' },
    { key: 'bond_requirement',    label: 'Bond requirement identified ($)', type: 'field_money',
      field: 'estimated_value',
      hint: 'Enter the bond amount required by the solicitation.' },
    { key: 'wage_determination',  label: 'Wage determination noted (Davis-Bacon)', type: 'wage_det' },
    { key: 'market_research',     label: 'Market research complete',      type: 'simple' },
    { key: 'site_visit',          label: 'Site visit attended (or N/A)',  type: 'site_visit' },
    { key: 'cost_pricing_model',  label: 'Cost/pricing model uploaded',   type: 'cost_model' },
  ],
  proposal_writing: [
    { key: 'technical_approach',  label: 'Technical approach written',    type: 'simple' },
    { key: 'pricing_narrative',   label: 'Pricing narrative written',     type: 'simple' },
    { key: 'all_sections_drafted', label: 'All sections drafted',         type: 'simple' },
    { key: 'internal_review',     label: 'Internal review complete',      type: 'simple' },
  ],
  bonding: [
    { key: 'bid_bond',            label: 'Bid bond acquired (if required)', type: 'doc_attach',
      docCategory: 'bond_form' },
    { key: 'performance_payment_bond', label: 'Performance/payment bond letter secured',
      type: 'doc_attach', docCategory: 'bond_form' },
  ],
  submitted: [
    { key: 'bid_submitted',       label: 'Bid packet submitted',          type: 'submission' },
    { key: 'timestamp_saved',     label: 'Submission timestamp + confirmation saved',
      type: 'field_date', field: 'bid_submitted_at' },
    { key: 'method_logged',       label: 'Submission method logged',      type: 'field_text',
      field: 'bid_submission_method' },
  ],
  proposal_verification: [
    { key: 'qa_monitored',        label: 'Q&A monitored',                  type: 'simple' },
    { key: 'amendments_checked',  label: 'Amendments checked for',        type: 'simple' },
    { key: 'no_pending_requests', label: 'No pending gov clarification requests', type: 'simple' },
  ],
  monitoring_for_award: [
    { key: 'decision_date_tracked', label: 'Award decision date tracked', type: 'field_date',
      field: 'award_date' },
    { key: 'follow_up_cadence',   label: 'Follow-up cadence set',         type: 'simple' },
  ],
};

// Helper — returns the tasks for a given pipeline+stage
export function tasksFor(pipeline: string, stage: string): TaskDef[] {
  if (pipeline === 'sources_sought') return SOURCES_SOUGHT_TASKS[stage] || [];
  if (pipeline === 'active')          return ACTIVE_TASKS[stage] || [];
  return [];
}

// Doc category display names
export const DOC_CATEGORY_LABELS: Record<string, string> = {
  solicitation: 'Solicitation',
  sow_pws: 'Statement of Work',
  wage_determination: 'Wage Determination',
  drawings: 'Drawings & Specs',
  attachment: 'Attachment / Exhibit',
  amendment: 'Amendment',
  qa: 'Q&A',
  site_visit: 'Site Visit Info',
  bond_form: 'Bond Form',
  past_performance: 'Past Performance',
  response: 'Our Response',
  capability_statement: 'Capability Statement',
  internal: 'Internal Notes',
  other: 'Other',
};

export const DOC_CATEGORY_ORDER: string[] = [
  'solicitation', 'sow_pws', 'wage_determination', 'drawings', 'attachment',
  'amendment', 'qa', 'site_visit', 'bond_form', 'past_performance',
  'response', 'capability_statement', 'internal', 'other',
];

export const DOC_CATEGORY_ICONS: Record<string, string> = {
  solicitation: '📋',
  sow_pws: '📝',
  wage_determination: '💰',
  drawings: '📐',
  attachment: '📎',
  amendment: '🔄',
  qa: '❓',
  site_visit: '🏗️',
  bond_form: '🔒',
  past_performance: '⭐',
  response: '📤',
  capability_statement: '🎯',
  internal: '🗂️',
  other: '📄',
};
